import { zodResolver } from '@hookform/resolvers/zod';
import { BillFor, DayEnum } from '@wonderschool/common-base-types';
import {
  ArrowLeftIcon,
  BusinessCoachCard,
  Button,
  ButtonLink,
  CurrencyInput,
  Divider,
  Input,
  Label,
  LinkOutIcon,
  MainContentLayout,
  Option,
  RadioButton,
  Select,
  Switch,
} from '@wonderschool/common-base-ui';
import dayjs from 'dayjs';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import utc from 'dayjs/plugin/utc';
import { memo, useCallback, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { z } from 'zod';
import LoadingComponent from '../../../Components/Shared/LoadingComponent';
import { convertToCents } from '../../../helpers/money';
import { capitalizeFirstLetter } from '../../../helpers/utils';
import { useOrganization } from '../../../hooks/useOrganizations';
import { RouteNameEnum } from '../../../navigation';
import { useRoutes } from '../../../navigation/navigationHooks';
import { useStudent } from '../../../students/studentsHooks';
import { NUMBERS_OF_THE_MONTH_OPTIONS } from '../../consts';
import { addInvoiceToStudent } from '../../enrollmentsAPI';
import {
  calculateEndBillingDate,
  calculateProrateAmount,
  calculateRecurringInvoiceTotal,
  calculateStartBillingDate,
  convertScheduledDaysToWeekdayNumbers,
  createInvitationRecipientsFromFamily,
  getNextInvoices,
  shouldCalculateProrateAmount,
} from '../../enrollmentsUtils';
import { BillingIntervalEnum, WEEKDAYS } from '../../enums';
import { InvitationRecipientType } from '../../types';
import OneTimeInvoicesForm from '../forms/OneTimeInvoicesForm';
import RecurringFeesForm from '../forms/RecurringFeesForm';
import EditProrateAmountModal from '../modals/EditProrateAmountModal';

dayjs.extend(utc);
dayjs.extend(isSameOrBefore);

// Schema validation with translations
const createSchema = (t: (key: string) => string) =>
  z
    .object({
      parentEmail: z
        .string()
        .email({ message: t('enrollments.errors.invalidEmail') })
        .min(1, { message: t('enrollments.errors.emailRequired') }),
      billingInterval: z.string().optional(),
      billFor: z.enum(['Prior', 'Next']).optional(),
      chargeDate: z.string().optional(),
      tuition: z.number({ invalid_type_error: t('enrollments.errors.copaymentPositive') }).optional(),
      recurringFees: z
        .array(
          z.object({
            type: z.string().min(1, t('enrollments.errors.feeTypeRequired')),
            category: z.string().min(1, t('enrollments.errors.feeCategoryRequired')),
            amount: z
              .number({ invalid_type_error: t('enrollments.errors.amountPositive') })
              .min(0, t('enrollments.errors.amountPositive'))
              .nonnegative(t('enrollments.errors.amountRequired')),
            amountType: z.string().min(1, t('enrollments.errors.amountRequired')),
          })
        )
        .optional(),
      oneTimeFees: z
        .array(
          z.object({
            name: z.string().min(1, t('enrollments.errors.feeTypeRequired')),
            amount: z
              .number({ invalid_type_error: t('enrollments.errors.amountPositive') })
              .min(0, t('enrollments.errors.amountPositive'))
              .nonnegative(t('enrollments.errors.amountRequired')),
            dueDate: z.string().min(1, t('enrollments.errors.dueDateRequired')),
          })
        )
        .optional(),
      subsidized: z.boolean().optional(),
      subsidyCopayment: z.boolean().optional(),
      planStartDate: z.string().min(1, t('enrollments.errors.planStartDateRequired')),
      planEndDate: z.string().optional(),
    })
    .superRefine((data, ctx) => {
      if (!data.subsidized || (data.subsidized && data.subsidyCopayment)) {
        if (!data.tuition) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: t('enrollments.errors.copaymentRequired'),
            path: ['tuition'],
          });
        }
        if (data.tuition && data.tuition <= 0) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: t('enrollments.errors.copaymentPositive'),
            path: ['tuition'],
          });
        }

        if (!data.billingInterval) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: t('enrollments.errors.billingIntervalRequired'),
            path: ['billingInterval'],
          });
        }

        if (!data.billFor) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: t('enrollments.errors.billForRequired'),
            path: ['billFor'],
          });
        }

        if (!data.chargeDate) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: t('enrollments.errors.chargeDateRequired'),
            path: ['chargeDate'],
          });
        }
      }
      return true;
    });

const HELP_ARTICLE_URL =
  'https://wonderschool.zendesk.com/hc/en-us/articles/32108790060951-Setting-up-recurring-plan-and-one-time-invoices-during-the-enrollment-process';

const EnrollmentInvoicePage = () => {
  const { t } = useTranslation();
  const params = useParams<{ studentId: string }>();
  const student = useStudent(params.studentId!);
  const organization = useOrganization();
  const { goBack, gotoRouteByName } = useRoutes();
  const [isSaving, setIsSaving] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [fixedProrateAmount, setFixedProrateAmount] = useState<number | null>(null);

  const schema = useMemo(() => createSchema(t), [t]);

  const invoiceRecipients = useMemo<InvitationRecipientType[]>(() => {
    return student ? createInvitationRecipientsFromFamily(student) : [];
  }, [student]);

  const tuitionAndFees = useMemo(() => {
    return (
      student?.enrollment?.tuitionAndFees ?? {
        tuition: {},
        fees: [],
      }
    );
  }, [student]);

  type FormValues = z.infer<typeof schema>;

  const billingIntervalOptions = useMemo(
    () => [
      { label: t('enrollments.billingWeekly'), value: BillingIntervalEnum.WEEKLY, name: t('week') },
      // { label: t('enrollments.billingBiWeekly'), value: BillingIntervalEnum.BIWEEKLY, name: t('bi-weekly') },
      { label: t('enrollments.billingMonthly'), value: BillingIntervalEnum.MONTHLY, name: t('month') },
    ],
    [t]
  );

  const billingPeriodOptions = {
    weekly: [
      { label: t('enrollments.priorWeek'), value: 'Prior' },
      { label: t('enrollments.nextWeek'), value: 'Next' },
    ],
    'bi-weekly': [
      { label: t('enrollments.priorTwoWeeks'), value: 'Prior' },
      { label: t('enrollments.nextTwoWeeks'), value: 'Next' },
    ],
    monthly: [
      { label: t('enrollments.priorMonth'), value: 'Prior' },
      { label: t('enrollments.nextMonth'), value: 'Next' },
    ],
  };

  const weekDaysOptions = useMemo(
    () => WEEKDAYS.map((day) => ({ label: t(capitalizeFirstLetter(day)), value: day })),
    [t]
  );

  const biweeklyOptions = useMemo(
    () =>
      WEEKDAYS.reduce((arr: Option[], day: DayEnum) => {
        return [
          ...arr,
          { label: t(`enrollments.first${capitalizeFirstLetter(day)}`), value: `1-${day}` },
          { label: t(`enrollments.second${capitalizeFirstLetter(day)}`), value: `2-${day}` },
        ];
      }, []),
    [t]
  );

  const chargeDateOptions = {
    weekly: weekDaysOptions,
    'bi-weekly': biweeklyOptions,
    monthly: NUMBERS_OF_THE_MONTH_OPTIONS,
  };

  const discountOptions = [
    { text: t('common.credit'), value: 'Credit' },
    { text: t('billing.dropdown.EmployeeDiscount'), value: 'Employee Discount' },
    { text: t('billing.dropdown.FinancialAid'), value: 'Financial Aid' },
    { text: t('enrollments.scholarship'), value: 'Scholarship' },
    { text: t('common.revenueSubsidy'), value: 'Subsidy' },
    { text: t('common.referral'), value: 'Referral' },
    { text: t('billing.dropdown.Sibling/FamilyDiscount'), value: 'Sibling/Family Discount' },
  ];

  const feeOptions = [
    { text: t('revenueDiaperFee'), value: 'Diaper Fee' },
    { text: t('billing.dropdown.MealFee'), value: 'Meal Fee' },
    { text: t('enrollments.materialFee'), value: 'Material Fee' },
  ];

  const [feeCategoryOptions, setFeeCategoryOptions] = useState({
    Discount: discountOptions,
    Fee: feeOptions,
  });

  const findSelectedOption = useCallback(
    (arr: Option[], value: string) => arr?.find((option) => option.value === value),
    []
  );

  const findSelectedOptionName = useCallback(
    (value: string) => billingIntervalOptions?.find((option) => option.value === value)?.name,
    [billingIntervalOptions]
  );

  const {
    control,
    handleSubmit,
    watch,
    register,
    formState: { errors },
    setValue,
    clearErrors,
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      parentEmail: invoiceRecipients[0]?.email ?? '',
      billingInterval: tuitionAndFees?.tuition?.billingInterval ?? '',
      billFor: tuitionAndFees?.tuition?.billFor ?? 'Next',
      chargeDate: tuitionAndFees?.tuition?.detailedTuition?.chargeDate ?? '',
      tuition: tuitionAndFees?.tuition?.detailedTuition?.tuition ?? 0,
      recurringFees: tuitionAndFees?.tuition?.detailedTuition?.fees ?? [],
      oneTimeFees: tuitionAndFees?.fees ?? [],
      subsidized: tuitionAndFees?.tuition?.detailedTuition?.subsidized ?? false,
      subsidyCopayment: tuitionAndFees?.tuition?.detailedTuition?.subsidyCopayment ?? true,
    },
  });

  const recurringInvoiceTotal = calculateRecurringInvoiceTotal(watch('recurringFees'), watch('tuition'));

  const selectedBillingInterval = watch('billingInterval') || '';
  const intervalBillingPeriodOptions = billingPeriodOptions[selectedBillingInterval] || [];
  const intervalChargeDatePeriodOptions = chargeDateOptions[selectedBillingInterval] || [];
  const chargeDate = watch('chargeDate');
  const billFor: 'Prior' | 'Next' = watch('billFor') || 'Next';
  const selectedChargeDate =
    chargeDateOptions[selectedBillingInterval]?.find((option) => option.value === chargeDate) || null;
  const planStartDate = watch('planStartDate');

  const goToSummary = useCallback(
    () => gotoRouteByName(RouteNameEnum.STUDENT_ENROLLMENT_SUMMARY, [{ name: 'studentId', value: params.studentId! }]),
    [gotoRouteByName, params.studentId]
  );

  const onSubmit = async (data: FormValues) => {
    setIsSaving(true);
    try {
      let tuition = {};

      if (!data.subsidized || (data.subsidized && data.subsidyCopayment)) {
        // Validate billing requirements are met before proceeding
        if (!data.chargeDate) {
          setIsSaving(false);
          console.error('Form validation bypassed: chargeDate is required for non-subsidized enrollments');
          return;
        }

        if (nextInvoices.length < 2) {
          setIsSaving(false);
          console.error('Cannot create billing plan: insufficient invoice data. chargeDate:', data.chargeDate);
          return;
        }

        const dueDate = nextInvoices[1].dueDate;
        const proratedDueDate = nextInvoices[0].dueDate;
        const proratedAmount = Math.max(nextInvoices[0].amount, 0);
        const proratedPeriod =
          nextInvoices.length > 0
            ? data.billingInterval === BillingIntervalEnum.WEEKLY
              ? t('enrollments.weekOf', { week: dayjs(nextInvoices[0].serviceStart).format('MMMM D') })
              : calculatedProrateAmount > 0
                ? `${dayjs(nextInvoices[0].serviceStart).format('MMMM D')} - ${dayjs(nextInvoices[0].serviceEnd).format('MMMM D, YYYY')}`
                : t('enrollments.monthOf', { month: dayjs(nextInvoices[0].serviceStart).format('MMMM') })
            : '';
        const firstServiceStart = nextInvoices[1].serviceStart;
        const firstServiceEnd = nextInvoices[1].serviceEnd;
        tuition = {
          amount: convertToCents(recurringInvoiceTotal),
          billingInterval: data.billingInterval,
          billFor: data.billFor,
          dueDate,
          proratedAmount,
          proratedPeriod,
          proratedDueDate,
          firstServiceStart,
          firstServiceEnd,
          isProrated: calculatedProrateAmount > 0,
          detailedTuition: {
            tuition: data.tuition,
            fees: data.recurringFees,
            planStartDate: data.planStartDate,
            ...(data.planEndDate && { planEndDate: data.planEndDate }), // Only include if not empty
            chargeDate: data.chargeDate,
            subsidized: data.subsidized,
            subsidyCopayment: data.subsidyCopayment,
          },
        };
      } else {
        tuition = {
          detailedTuition: {
            subsidized: data.subsidized,
            subsidyCopayment: data.subsidyCopayment,
          },
        };
      }
      const fees =
        data.oneTimeFees?.map((invoice) => {
          return { ...invoice, dueDate: new Date(invoice.dueDate) };
        }) || [];
      const tuitionAndFees = { tuition, fees };

      await addInvoiceToStudent(student, tuitionAndFees, organization.id);

      goToSummary();
    } catch (e) {
      console.error(e);
      setIsSaving(false);
    }
  };

  const onError = useCallback((errors: any, e: any) => console.error(errors, e), []);

  const currentParentEmail = watch('parentEmail');
  const setParentEmail = useCallback(
    (parent: any) => {
      setValue('parentEmail', currentParentEmail === parent.email ? '' : parent.email || '');
    },
    [currentParentEmail, setValue]
  );

  const startBillingDate = useMemo(() => {
    if (chargeDate && selectedBillingInterval && planStartDate && billFor) {
      return calculateStartBillingDate(selectedBillingInterval as BillingIntervalEnum, dayjs(planStartDate).toDate());
    } else {
      return null;
    }
  }, [chargeDate, selectedBillingInterval, planStartDate, billFor]);

  const endBillingDate = useMemo(() => {
    if (chargeDate && selectedBillingInterval && planStartDate && billFor) {
      return calculateEndBillingDate(
        chargeDate,
        selectedBillingInterval as BillingIntervalEnum,
        dayjs(planStartDate).toDate()
      );
    } else {
      return null;
    }
  }, [chargeDate, selectedBillingInterval, planStartDate, billFor]);

  const endEnrollmentRange = useMemo(() => {
    if (billFor === 'Prior') {
      if (selectedBillingInterval === BillingIntervalEnum.WEEKLY) {
        return dayjs(startBillingDate).subtract(1, 'day').toDate();
      } else {
        return dayjs(endBillingDate).toDate();
      }
    }
    return endBillingDate;
  }, [billFor, endBillingDate, selectedBillingInterval, startBillingDate]);

  const weekdayNumbers = useMemo(
    () => convertScheduledDaysToWeekdayNumbers(student?.enrollment?.scheduledDays) || [],
    [student?.enrollment?.scheduledDays]
  );

  const shouldCalculateProrate = shouldCalculateProrateAmount(
    chargeDate || '',
    selectedBillingInterval as BillingIntervalEnum,
    dayjs(planStartDate).toDate()
  );

  const calculatedProrateAmount = useMemo(() => {
    if (typeof fixedProrateAmount === 'number') {
      return fixedProrateAmount;
    }
    return shouldCalculateProrate
      ? calculateProrateAmount(
          weekdayNumbers,
          startBillingDate,
          endBillingDate,
          dayjs(planStartDate).toDate(),
          endEnrollmentRange,
          recurringInvoiceTotal || 0,
          selectedBillingInterval as BillingIntervalEnum
        )
      : 0;
  }, [
    shouldCalculateProrate,
    weekdayNumbers,
    startBillingDate,
    endBillingDate,
    planStartDate,
    endEnrollmentRange,
    recurringInvoiceTotal,
    fixedProrateAmount,
    selectedBillingInterval,
  ]);

  if (!student) return <LoadingComponent />;

  const saveProrateAmount = (amount) => {
    if (amount) {
      setFixedProrateAmount(parseFloat(amount));
    }
    setIsModalOpen(false);
  };

  const nextInvoices = getNextInvoices(
    dayjs(planStartDate).toDate(),
    recurringInvoiceTotal,
    selectedBillingInterval as BillingIntervalEnum,
    chargeDate,
    shouldCalculateProrate,
    calculatedProrateAmount,
    BillFor.Prior
  );

  return (
    <MainContentLayout title={t('enrollments.invoicesPageTitle')}>
      <BusinessCoachCard text={t('enrollments.coachMessage')} />
      <form onSubmit={handleSubmit(onSubmit, onError)} className="space-y-6 pt-6">
        <div className="space-y-4">
          <h2 className="text-xl font-semibold">{t('common.primaryPayer')}</h2>
          <div className="text-base">{t('enrollments.primaryPayerDescription')}</div>
          {invoiceRecipients.map((parent, index) => (
            <div key={index}>
              <label className="flex items-center space-x-2">
                <RadioButton
                  disabled={!parent.email || invoiceRecipients.length <= 1}
                  checked={currentParentEmail === parent.email}
                  className="form-checkbox"
                  onChange={() => setParentEmail(parent)}
                />
                <div className="flex flex-col">
                  <Label>{parent.displayName}</Label>
                  <span className="text-xs">{parent.email}</span>
                </div>
              </label>
            </div>
          ))}
          {errors.parentEmail && <p className="text-sm text-red-950">{errors.parentEmail.message}</p>}
        </div>

        <Divider />

        {/* Recurring Invoice Section */}
        <div className="space-y-4">
          <div className="flex items-center gap-4">
            <h2 className="text-xl font-semibold">{t('enrollments.recurringInvoice')}</h2>
            <ButtonLink
              postIcon={<LinkOutIcon className="size-4" />}
              onClick={() => window.open(HELP_ARTICLE_URL, '_blank')}
            >
              {t('enrollments.viewArticle')}
            </ButtonLink>
          </div>
          <div className="text-base">{t('enrollments.recurringInvoiceInstructions')}</div>
          <div className="flex items-center space-x-2">
            <Switch
              {...register('subsidized')}
              label={t('enrollments.receivingSubsidizedCare')}
              wrapperClasses="flex-row-reverse"
            />
          </div>

          {watch('subsidized') && (
            <div className="flex items-center space-x-2">
              <Switch
                {...register('subsidyCopayment')}
                label={t('enrollments.paySubsidyCopayment')}
                wrapperClasses="flex-row-reverse"
              />
            </div>
          )}

          {(!watch('subsidized') || (watch('subsidized') && watch('subsidyCopayment'))) && (
            <div className="grid grid-cols-1 gap-4 pt-2 md:grid-cols-2">
              <div>
                <Controller
                  name="planStartDate"
                  control={control}
                  rules={{
                    required: t('enrollments.invoicePlan.startDateRequired'),
                    validate: {
                      maxDate: (date) =>
                        dayjs(date).isBefore(dayjs(dayjs().add(2, 'year'))) || t('enrollments.errors.maxDate'),
                    },
                  }}
                  render={({ field }) => (
                    <Input
                      {...field}
                      required
                      value={field.value ?? ''}
                      label={t('invoicePlan.startDate')}
                      type="date"
                      inputType="date"
                      min={dayjs().format('YYYY-MM-DD')}
                      error={errors.planStartDate?.message}
                    />
                  )}
                />
              </div>
              <div>
                <Controller
                  name="planEndDate"
                  control={control}
                  rules={{
                    validate: {
                      maxDate: (date) =>
                        dayjs(date).isBefore(dayjs(dayjs().add(2, 'year'))) || t('enrollments.errors.maxDate'),
                    },
                  }}
                  render={({ field }) => (
                    <Input
                      {...field}
                      value={field.value ?? ''}
                      label={t('invoicePlan.endDate')}
                      type="date"
                      inputType="date"
                      min={dayjs().format('YYYY-MM-DD')}
                      error={errors.planEndDate?.message}
                    />
                  )}
                />
              </div>
              <Controller
                name="billingInterval"
                control={control}
                render={() => (
                  <Select
                    label={t('enrollments.enrollmentInfoBillingIntervalLabel')}
                    options={billingIntervalOptions}
                    required
                    error={errors.billingInterval?.message}
                    onChange={(option: Option) => {
                      setValue('billingInterval', option.value);
                      setValue('chargeDate', undefined);
                      clearErrors('billingInterval');
                    }}
                    value={findSelectedOption(billingIntervalOptions, selectedBillingInterval)}
                  />
                )}
              />

              <div className="flex flex-col gap-2">
                <Label required>{t('enrollments.billFor')}</Label>
                <div className="flex items-center">
                  <Button
                    primary={!billFor || billFor === 'Prior'}
                    onClick={() => {
                      setValue('billFor', intervalBillingPeriodOptions[0].value as 'Prior' | 'Next');
                      clearErrors('billFor');
                    }}
                    disabled={!selectedBillingInterval}
                    extraClasses="rounded-r-none"
                  >
                    {intervalBillingPeriodOptions[0]?.label || 'Prior'}
                  </Button>
                  <Button
                    primary={billFor === 'Next'}
                    onClick={() => {
                      setValue('billFor', intervalBillingPeriodOptions[1].value as 'Prior' | 'Next');
                      clearErrors('billFor');
                    }}
                    disabled={!selectedBillingInterval}
                    extraClasses="rounded-l-none"
                  >
                    {intervalBillingPeriodOptions[1]?.label || 'Next'}
                  </Button>
                </div>
              </div>

              <div>
                {selectedBillingInterval === BillingIntervalEnum.BIWEEKLY ? (
                  <Input
                    type="date"
                    inputType="date"
                    {...register('chargeDate')}
                    label={t('enrollments.dueDate')}
                    className="h-9 w-full rounded-md border p-2"
                    value={watch('chargeDate')}
                    disabled={!selectedBillingInterval}
                    error={errors.chargeDate?.message}
                    required
                    min={dayjs.utc(planStartDate).format('YYYY-MM-DD')}
                  />
                ) : (
                  <Controller
                    name="chargeDate"
                    control={control}
                    render={() => (
                      <Select
                        extraClasses="capitalize"
                        label={t('enrollments.dueDate')}
                        options={intervalChargeDatePeriodOptions}
                        required
                        error={errors.chargeDate?.message}
                        onChange={(option: Option) => {
                          setValue('chargeDate', option.value);
                          clearErrors('chargeDate');
                        }}
                        value={selectedChargeDate}
                        disabled={!selectedBillingInterval}
                      />
                    )}
                  />
                )}
                <div className="text-xs">{t('enrollments.chargeDateNote')}</div>
              </div>

              <div className="flex flex-col gap-2">
                <Controller
                  name={'tuition'}
                  control={control}
                  render={() => (
                    <CurrencyInput
                      label={t('enrollments.enrollmentInfoTuitionLabel')}
                      onChange={({ target }) => {
                        const value = target.value.replace(',', '');
                        setValue('tuition', parseFloat(value));
                        clearErrors('tuition');
                      }}
                      value={(() => {
                        const tuitionValue = watch('tuition');
                        return tuitionValue && typeof tuitionValue === 'number' && tuitionValue > 0
                          ? tuitionValue.toString()
                          : '';
                      })()}
                      disabled={!selectedBillingInterval}
                      required
                      postIcon={
                        selectedBillingInterval ? (
                          <span className="text-gray-800">/ {findSelectedOptionName(selectedBillingInterval)}</span>
                        ) : undefined
                      }
                    />
                  )}
                />
                {errors.tuition && <p className="text-sm text-red-950">{errors.tuition?.message}</p>}
              </div>
            </div>
          )}
        </div>

        <Divider />

        {(!watch('subsidized') || (watch('subsidized') && watch('subsidyCopayment'))) && (
          <RecurringFeesForm
            control={control}
            setValue={setValue}
            clearErrors={clearErrors}
            feeCategoryOptions={feeCategoryOptions}
            setFeeCategoryOptions={setFeeCategoryOptions}
            watch={watch}
            selectedBillingInterval={selectedBillingInterval}
            findSelectedOptionName={findSelectedOptionName}
            register={register}
            errors={errors}
            recurringInvoiceTotal={recurringInvoiceTotal}
            calculatedProrateAmount={calculatedProrateAmount}
            startDate={planStartDate}
            nextInvoices={nextInvoices}
            setIsModalOpen={setIsModalOpen}
          />
        )}

        <OneTimeInvoicesForm
          control={control}
          setValue={setValue}
          clearErrors={clearErrors}
          feeCategoryOptions={feeCategoryOptions}
          watch={watch}
          register={register}
          errors={errors}
        />

        <div className="mt-6 flex justify-between">
          <Button
            onClick={() => {
              goBack();
            }}
            data-testid="es-goback-btn"
            preIcon={<ArrowLeftIcon className="size-6" />}
          >
            {t('common.previous')}
          </Button>
          <div className="flex gap-4">
            <Button onClick={goToSummary}>{t('enrollments.setUpInvoicesLater')}</Button>
            <Button primary type="submit" loading={isSaving}>
              {t('enrollments.review')}
            </Button>
          </div>
        </div>
      </form>
      <EditProrateAmountModal
        amount={calculatedProrateAmount}
        billingInterval={selectedBillingInterval}
        enrollmentDate={dayjs.utc(planStartDate).format('MM/DD/YYYY')}
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSave={saveProrateAmount}
      />
    </MainContentLayout>
  );
};

export default memo(EnrollmentInvoicePage);
